import React, { useEffect, useMemo, useState } from "react";
import MUIDataTable from "mui-datatables";
import { Icon } from "@iconify/react";
import { useDispatch, useSelector } from "react-redux";
import { getEvents, getEventDetails } from "../Redux/Reducers/AuthSlice";
import DefaultAvatar from "../otherImages/default.png";
import useTableUrlState from "../hook/useTableUrlState";
import { getPaginationTotal, getRowsPerPageOptions } from "../helper/tablePagination";
import { getSortOrder, sortTableData } from "../helper/tableSort";

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const measurementFields = [
  ["belt_size", "Belt Size"],
  ["coat_fit", "Coat Fit"],
  ["coat_size", "Coat Size"],
  ["pant_outseam_measurement", "Pant Outseam Measurement"],
  ["pant_waist_measurement", "Pant Waist Measurement"],
  ["shirt_size", "Shirt Size"],
  ["shoe_size", "Shoe Size"],
  ["tie_size", "Tie Size"],
];

const getMeasurementFields = (measurements) => {
  const knownKeys = new Set(measurementFields.map(([key]) => key));
  const additionalFields = Object.keys(measurements || {})
    .filter((key) => !knownKeys.has(key))
    .map((key) => [key, key.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase())]);

  return [...measurementFields, ...additionalFields];
};

const EventsDataTable = () => {
  const dispatch = useDispatch();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [expandedMemberId, setExpandedMemberId] = useState(null);
  const [
    { filter, statusFilter, page, rowsPerPage, searchText, sortName, sortDirection },
    setTableState,
  ] = useTableUrlState({
    filter: "all",
    statusFilter: "all",
    page: 0,
    rowsPerPage: 12,
    searchText: "",
    sortName: "",
    sortDirection: "",
  });
  const [isSearchUpdate, setIsSearchUpdate] = useState(false);
  const {
    events,
    eventsPagination,
    selectedEvent,
    eventsLoading,
    eventDetailsLoading,
    eventsError,
  } = useSelector((state) => state.auth);

  const eventsTotal = getPaginationTotal(eventsPagination, events.length);
  const hasSearch = Boolean(searchText?.trim());
  const isLocalFilteredView =
    !hasSearch && (filter === "monthly" || statusFilter !== "all");
  const requestPage = isLocalFilteredView ? 1 : page + 1;
  const requestPerPage = isLocalFilteredView
    ? Math.max(eventsTotal, rowsPerPage)
    : rowsPerPage;

  useEffect(() => {
    dispatch(getEvents({
      page: requestPage,
      perPage: requestPerPage,
      search: hasSearch ? searchText : "",
    }));
  }, [dispatch, hasSearch, requestPage, requestPerPage, searchText]);

  useEffect(() => {
    if (!eventsLoading) setIsSearchUpdate(false);
  }, [eventsLoading]);

  const getCalculatedStatus = (dateValue) => {
    if (!dateValue) return "-";
    const eventDate = new Date(dateValue);
    const today = new Date();
    eventDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return eventDate >= today ? "Upcoming" : "Completed";
  };

  const tableData = useMemo(() => {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const filteredEvents = events.filter((event) => {
      const matchesMonth =
        filter !== "monthly" ||
        (() => {
          const eventDate = new Date(event.date);
          return eventDate.getMonth() === currentMonth && eventDate.getFullYear() === currentYear;
        })();
      const matchesStatus =
        statusFilter === "all" ||
        getCalculatedStatus(event.date).toLowerCase() === statusFilter;
      return matchesMonth && matchesStatus;
    });

    return sortTableData(
      filteredEvents.map((event) => ({
        ...event,
        creator_name: event?.organizer?.name || event?.creator?.name || "-",
        calculated_status: getCalculatedStatus(event.date),
      })),
      sortName,
      sortDirection,
      {
        organizer: (event) => event.creator_name,
        status: (event) => event.calculated_status,
      }
    );
  }, [events, filter, sortDirection, sortName, statusFilter]);

  const handleView = (eventId) => {
    setExpandedMemberId(null);
    setDetailsOpen(true);
    dispatch(getEventDetails(eventId));
  };

  const columns = [
    {
      name: "name",
      label: "Event",
      options: {
        customBodyRender: (value, tableMeta) => {
          const event = tableData[tableMeta.rowIndex];
          return (
            <div className="d-flex align-items-center gap-2">
              <img
                src={event?.image_url || DefaultAvatar}
                alt={value || "Event"}
                style={{ width: "35px", height: "35px", borderRadius: "50%", objectFit: "cover" }}
                onError={(e) => { e.currentTarget.src = DefaultAvatar; }}
              />
              <span className="fw-medium">{value || "-"}</span>
            </div>
          );
        },
      },
    },
    {
      name: "creator_name",
      label: "Creator Name",
      options: {
        customBodyRender: (value, tableMeta) => {
          return value || "-";
        },
      },
    },
    {
      name: "date",
      label: "Date",
      options: { customBodyRender: (value) => formatDate(value) },
    },
    { name: "location", label: "Location" },
    {
      name: "calculated_status",
      label: "Status",
      options: {
        customBodyRender: (value) => (
          <span
            className={`col-payment-status ${
              value === "Upcoming"
                ? "bg-primary-100 text-primary-800"
                : "bg-success-100 text-success-800"
            }`}
            style={{ display: "inline-block", padding: "4px 12px", borderRadius: "20px" }}
          >
            {value || "-"}
          </span>
        ),
      },
    },
    {
      name: "id",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => (
          <div className="d-flex justify-content-center">
            <Icon
              className="editBtn"
              icon="boxicons:eye"
              width="24"
              height="24"
              style={{ cursor: "pointer" }}
              onClick={() => handleView(value)}
            />
          </div>
        ),
      },
    },
  ];

  const options = {
    selectableRows: "none",
    rowsPerPage,
    rowsPerPageOptions: getRowsPerPageOptions(
      isLocalFilteredView ? tableData.length : eventsTotal
    ),
    responsive: "standard",
    elevation: 0,
    print: false,
    download: false,
    viewColumns: false,
    filter: false,
    search: true,
    customToolbar: () => (
      <div className="status-filter-dropdown2">
        <select
          className="form-select"
          value={statusFilter}
          onChange={(event) =>
            setTableState({ statusFilter: event.target.value, page: 0 })
          }
        >
          <option value="all">All Status</option>
          <option value="upcoming">Upcoming</option>
          <option value="completed">Completed</option>
        </select>
      </div>
    ),
    sortOrder: getSortOrder(sortName, sortDirection),
    onColumnSortChange: (changedColumn, direction) => {
      setTableState({
        sortName: direction === "none" ? "" : changedColumn,
        sortDirection: direction === "none" ? "" : direction,
      });
    },
    searchText,
    onSearchChange: (searchValue) => {
      setIsSearchUpdate(true);
      setTableState({ searchText: searchValue || "", page: 0 });
    },
    serverSide: !isLocalFilteredView,
    count: isLocalFilteredView ? tableData.length : eventsTotal,
    page,
    onChangePage: (newPage) => setTableState({ page: newPage }),
    onChangeRowsPerPage: (numberOfRows) =>
      setTableState({ rowsPerPage: numberOfRows, page: 0 }),
  };

  useEffect(() => {
    const tableTotal = isLocalFilteredView ? tableData.length : eventsTotal;
    if (page > 0 && tableTotal <= page * rowsPerPage) {
      setTableState({ page: 0 });
    }
  }, [eventsTotal, isLocalFilteredView, page, rowsPerPage, setTableState, tableData.length]);

  if (eventsLoading && !events.length && !isSearchUpdate) {
    return (
      <div className="card basic-data-table">
        <div className="card-body text-center py-5">
          <div className="spinner-border text-primary" role="status" />
          <p className="mt-3 mb-0">Loading events...</p>
        </div>
      </div>
    );
  }

  if (eventsError && !detailsOpen) {
    return (
      <div className="card basic-data-table">
        <div className="card-body text-center py-5 text-danger">
          <Icon icon="material-symbols:error-outline" width="48" height="48" />
          <p className="mt-3">{eventsError}</p>
          <button className="btn btn-primary" onClick={() => dispatch(getEvents({
            page: requestPage,
            perPage: requestPerPage,
            search: hasSearch ? searchText : "",
          }))}>Retry</button>
        </div>
      </div>
    );
  }

  const event = selectedEvent?.event;
  const members = selectedEvent?.members || [];
  const looks = selectedEvent?.looks || [];

  return (
    <>
      <div className="card basic-data-table productTable">
        <div className="card-header d-flex justify-content-end align-items-center">
          <div className="custom-toggle">
            <div className="toggle-container">
              <div className={`toggle-indicator ${filter === "all" ? "right" : "left"}`} />
              <div
                className={`toggle-option ${filter === "monthly" ? "active" : ""}`}
                onClick={() => setTableState({ filter: "monthly", page: 0 })}
              >
                <Icon icon="mdi:calendar-month" width="22" />
                <span>This Month</span>
              </div>
              <div
                className={`toggle-option ${filter === "all" ? "active" : ""}`}
                onClick={() => setTableState({ filter: "all", page: 0 })}
              >
                <Icon icon="mdi:calendar-today" width="25" />
                <span>All Time</span>
              </div>
            </div>
          </div>
        </div>
        <div className="card-body">
          <MUIDataTable data={tableData} columns={columns} options={options} className="overflow-hidden packageTable" />
        </div>
      </div>

      {detailsOpen && (
        <div className="modal fade show d-block free-tape-detail-modal" tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-dialog-centered modal-xl" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Event Details</h5>
              </div>
              <div className="modal-body">
                {eventDetailsLoading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status" />
                  </div>
                ) : eventsError ? (
                  <p className="text-danger text-center py-4 mb-0">{eventsError}</p>
                ) : event ? (
                  <>
                    <div className="row g-3">
                      {event.image_url && (
                        <div className="col-12">
                          <img src={event.image_url} alt={event.name} className="event-detail-image" />
                        </div>
                      )}
                      {[
                        ["Name", event.name],
                        ["Type", event.type],
                        ["Date", formatDate(event.date)],
                        ["Location", event.location],
                        ["Status", getCalculatedStatus(event.date)],
                        ["Creator Name", event.organizer?.name],
                        ["Creator Email", event.organizer?.email],
                        ["Description", event.description],
                      ].map(([label, value]) => (
                        <div className={label === "Description" ? "col-12" : "col-md-6"} key={label}>
                          <div className="free-tape-detail-item">
                            <span>{label}</span>
                            <strong>{value || "-"}</strong>
                          </div>
                        </div>
                      ))}
                    </div>

                    <h5 className="mb-3 event-heading">Members</h5>
                    <div className="table-responsive event-members-table-wrap">
                      <table className="table bordered-table mb-0 event-members-table">
                        <thead><tr><th>Name</th><th>Email</th><th>Phone</th><th>Role</th><th>Status</th><th><span className="visually-hidden">Measurements</span></th></tr></thead>
                        <tbody>
                          {members.map((member, index) => {
                            const memberKey = member.id ?? index;
                            const isExpanded = expandedMemberId === memberKey;
                            return (
                            <React.Fragment key={memberKey}>
                            <tr className={isExpanded ? "event-member-row is-expanded" : "event-member-row"}>
                              <td>{member.name || "-"}</td>
                              <td>{member.email || "-"}</td>
                              <td>{member.phone || "-"}</td>
                              <td>{member.role || "-"}</td>
                              <td>
                                <span className="badge bg-black member-status-badge">
                                  {member.status || "-"}
                                </span>
                              </td>
                              <td className="event-member-action-cell">
                                <button
                                  type="button"
                                  className="event-member-toggle"
                                  aria-label={`${isExpanded ? "Hide" : "Show"} measurements for ${member.name || "member"}`}
                                  aria-expanded={isExpanded}
                                  aria-controls={`member-measurements-${memberKey}`}
                                  onClick={() => setExpandedMemberId(isExpanded ? null : memberKey)}
                                >
                                  <Icon icon="mdi:chevron-down" width="22" height="22" aria-hidden="true" />
                                </button>
                              </td>
                            </tr>
                            <tr className={`event-member-detail-row ${isExpanded ? "is-expanded" : ""}`}>
                              <td colSpan="6">
                                <div id={`member-measurements-${memberKey}`} className="event-member-panel" aria-hidden={!isExpanded}>
                                  <div className="event-member-panel-inner">
                                    <div className="event-member-panel-content">
                                      <div className="event-member-panel-title">Measurements</div>
                                      <table className="event-measurements-table">
                                        <thead><tr><th scope="col">Measurement</th><th scope="col">Value</th></tr></thead>
                                        <tbody>
                                          {getMeasurementFields(member.measurements).map(([key, label]) => (
                                            <tr key={key}>
                                              <th scope="row">{label}</th>
                                              <td>{member.measurements?.[key] === null || member.measurements?.[key] === undefined || member.measurements?.[key] === "" ? "-" : String(member.measurements[key])}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                            </React.Fragment>
                          );})}
                          {!members.length && <tr><td colSpan="6" className="text-center">No members</td></tr>}
                        </tbody>
                      </table>
                    </div>

                    <h5 className="event-heading mb-3">Looks</h5>
                    <div className="row g-3">
                      {looks.map((look) => (
                        <div className="col-md-6 col-lg-4" key={look.id}>
                          <div className="free-tape-detail-item">
                            <img src={look.image_url || DefaultAvatar} alt={look.name} className="event-look-image" onError={(e) => { e.currentTarget.src = DefaultAvatar; }} />
                            <strong className="mt-2">{look.name || "-"}</strong>
                            {/* <span className="mt-1 mb-0">{look.color || "-"} · ${look.price || 0}</span> */}
                          </div>
                        </div>
                      ))}
                      {!looks.length && <div className="col-12 text-center">No looks</div>}
                    </div>
                  </>
                ) : null}
              </div>
              <div className="modal-footer">
                <button type="button" className="designBtn" onClick={() => setDetailsOpen(false)}>Close</button>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" onClick={() => setDetailsOpen(false)} />
        </div>
      )}
    </>
  );
};

export default EventsDataTable;
